import React from 'react';
import { Shield, Eye, Lock, Users, FileText, Mail } from 'lucide-react';

export default function PrivacyPolicy() {
  const sections = [
    {
      id: '1',
      title: 'Information We Collect',
      icon: <Eye className="w-6 h-6" />,
      content: (
        <div>
          <p className="mb-4 text-gray-600">We may collect and process the following data about you:</p>
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
              <h4 className="font-semibold text-blue-800 mb-2">Personal Information</h4>
              <p className="text-blue-700">We collect personal details such as your name, email address, shipping address, phone number, and payment information when you make a purchase, create an account, or contact us.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
              <h4 className="font-semibold text-green-800 mb-2">Non-Personal Information</h4>
              <p className="text-green-700">We may collect non-personal data such as browser type, operating system, IP address, and browsing behavior to improve our website and services.</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
              <h4 className="font-semibold text-yellow-800 mb-2">Cookies</h4>
              <p className="text-yellow-700">Our website uses cookies to distinguish you from other users of our website. This helps us to provide you with a good experience when you browse our website and also allows us to improve our site.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: '2',
      title: 'How We Use Your Information',
      icon: <FileText className="w-6 h-6" />,
      content: (
        <div>
          <p className="mb-4 text-gray-600">We use the information we collect in the following ways:</p>
          <div className="grid gap-4 md:grid-cols-1">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                To Process Orders
              </h4>
              <p className="text-purple-700">We use your personal information to process and fulfill your orders, including to manage payments, fees and charges.</p>
            </div>
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                To Communicate
              </h4>
              <p className="text-indigo-700">We use your contact information to send you updates about your order, respond to inquiries, and send promotional materials if you have opted in.</p>
            </div>
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-teal-800 mb-2 flex items-center gap-2">
                <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                To Improve Our Services
              </h4>
              <p className="text-teal-700">We analyze non-personal information to understand user behavior and enhance our website's performance, and to administer and protect our business and this website.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: '3',
      title: 'Information Sharing',
      icon: <Users className="w-6 h-6" />,
      content: (
        <div>
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-6">
            <p className="text-red-800 font-medium">We do not sell, trade, or otherwise transfer to outside parties your personally identifiable information unless we provide users with advance notice.</p>
          </div>
          <p className="mb-4 text-gray-600">This does not include:</p>
          <div className="space-y-4">
            <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-3 h-3 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Third-Party Service Providers</h4>
                <p className="text-gray-600">We may share your information with trusted third-party service providers who assist us in operating our website, processing payments, and delivering orders, so long as those parties agree to keep this information confidential.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-3 h-3 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Legal Requirements</h4>
                <p className="text-gray-600">We may also release information when it's release is appropriate to comply with the law, enforce our site policies, or protect ours or others' rights, property or safety.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: '4',
      title: 'Data Security',
      icon: <Lock className="w-6 h-6" />,
      content: (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-green-800">Your Data is Protected</h3>
          </div>
          <p className="text-green-700 leading-relaxed">
            We implement a variety of appropriate security measures to maintain the safety of your personal information from unauthorized access, alteration, disclosure, or destruction. Your personal information is contained behind secured networks and is only accessible by a limited number of persons who have special access rights to such systems, and are required to keep the information confidential.
          </p>
        </div>
      )
    },
    {
      id: '5',
      title: 'Your Rights',
      icon: <Shield className="w-6 h-6" />,
      content: (
        <div>
          <p className="mb-6 text-gray-600">You have the following rights in relation to your personal data:</p>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow border-t-4 border-blue-400">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Access & Correction</h4>
              <p className="text-gray-600 text-sm">You have the right to access and correct your personal information. You can update your account details through our website.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow border-t-4 border-purple-400">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Opt-Out</h4>
              <p className="text-gray-600 text-sm">You can opt-out of receiving promotional emails by following the unsubscribe instructions in the emails. You may also contact us to have your information removed from our mailing lists.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow border-t-4 border-green-400">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Data Portability</h4>
              <p className="text-gray-600 text-sm">You have the right to request the transfer of your personal data to you or to a third party.</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-lg">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Privacy Policy
              </h1>
              <p className="text-gray-600 mt-2">Last Updated: 24 September 2025</p>
            </div>
          </div>
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl border border-blue-200">
            <p className="text-gray-700 leading-relaxed">
              This website is Owned & Operated by <strong className="text-blue-600">Texpidea Technologies Pvt. Ltd.</strong>. 
              We are committed to protecting your privacy and ensuring that your personal information is handled in a safe and responsible manner.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-12">
          {sections.map((section) => (
            <div key={section.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white shadow-md">
                  {section.icon}
                </div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {section.id}. {section.title}
                </h2>
              </div>
              <div className="ml-16">
                {section.content}
              </div>
            </div>
          ))}

          {/* Additional Sections */}
          <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center text-white shadow-md">
                <FileText className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">6. Changes to This Policy</h2>
            </div>
            <div className="ml-16">
              <div className="bg-orange-50 border border-orange-200 p-6 rounded-lg">
                <p className="text-orange-800 leading-relaxed">
                  We may update this Privacy Policy from time to time. Any changes will be posted on this page, 
                  and the revised date will be indicated at the top of the policy. We encourage you to review this 
                  privacy policy periodically to stay informed about how we are protecting the personal information we collect.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-xl flex items-center justify-center text-white shadow-md">
                <Mail className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">7. Contact Us</h2>
            </div>
            <div className="ml-16">
              <div className="bg-gradient-to-r from-green-50 to-teal-50 p-6 rounded-lg border border-green-200">
                <p className="text-green-800 leading-relaxed">
                  If you have any questions regarding this privacy policy, you may contact us using the information on our website. <a 
  href="https://www.texpidea.com" 
  target="_blank" 
  rel="noopener noreferrer"
  
>
  <strong>www.texpidea.com</strong>
</a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-lg">
            <Shield className="w-5 h-5 text-blue-500" />
            <span className="text-gray-600 font-medium">Your privacy is our priority</span>
          </div>
        </div>
      </div>
    </div>
  );
}